﻿import { useState, useEffect } from "react";
import { useQuery } from "react-query";
import { useSelector } from "react-redux";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { useNavigate } from "react-router-dom";
import Select from "react-select";

import { NoOptionsMessage } from "@/components/ReactSelect/NoOptions";
import { getDashboardUsedBC } from "@/api/Dashboard/dashboard";
import Fetching from "@/components/Fetching/Fetching";
import { validRoles, formatShortNumber, formatMilesDecimalNew } from "@/services/Utils";

import classes from "./dashboard-used.module.scss";

const DashboardUsedBC = ({ filters }) => {
	const navigate = useNavigate();
	const userRoles = JSON.parse(localStorage.getItem("roles"));
	const isManager = validRoles(userRoles, ["administrador", "gerente_agricola"]);

	const { estate_parent_id, estate_id, type_currency } = useSelector((state) => state.auth.filter);

	const [type, setType] = useState("total");
	const [selectedSpecies, setSelectedSpecies] = useState([]);
	const [selectedVarieties, setSelectedVarieties] = useState([]);
	const [chartData, setChartData] = useState([]);
	const [finalIds, setFinalIds] = useState([]);
	const [allSpecies, setAllSpecies] = useState([]);
	const [allVarieties, setAllVarieties] = useState([]);

	const { data, isLoading } = useQuery(["dashboard_used_bc", { ...filters, estate_parent_id, estate_id, type_currency }], () =>
		getDashboardUsedBC({
			...filters,
			estate_parent_id,
			estate_id,
			type_currency,
		}),
	);

	useEffect(() => {
		if (!data) return;

		const chart = [];
		const ids = [];

		setAllSpecies(data.species);
		setAllVarieties(data.varieties);

		let dataset = data.used;

		const selectedSpeciesValues = selectedSpecies.map((s) => String(s.value));
		const selectedVarietiesValues = selectedVarieties.map((v) => v.value);

		dataset = dataset.filter((item) => {
			const matchSpecie = selectedSpeciesValues.length === 0 || selectedSpeciesValues.includes(String(item.specie));
			const matchVariety = selectedVarietiesValues.length === 0 || selectedVarietiesValues.includes(item.variety);
			return matchSpecie && matchVariety;
		});

		dataset.forEach((item) => {
			let label = item.business_center_name;
			if (type === "ha") {
				label = `${item.business_center_name} (${formatMilesDecimalNew(item.CENTROS_COSTO_AREA.toString().replace(".", ","))} Ha)`;
			}

			const value = type === "ha" ? item.global_price / (item.CENTROS_COSTO_AREA <= 0 ? 1 : item.CENTROS_COSTO_AREA) : item.global_price;

			chart.push({
				name: label,
				value,
				fill: item.backgroundColor,
			});

			ids.push(item.business_center_id);
		});

		setChartData(chart);
		setFinalIds(ids);
	}, [data, type, selectedSpecies, selectedVarieties]);

	if (!isManager) return null;

	const filteredVarieties = selectedSpecies.length > 0 ? allVarieties.filter((v) => selectedSpecies.some((s) => String(s.value) === String(v.specie))) : [];

	return (
		<>
			{isLoading ? (
				<Fetching topMargin="5em" bottomMargin="5em" />
			) : (
				<>
					<div className={classes.toggleWrapper}>
						<button className={`${classes.toggleButton} ${type === "total" ? classes.active : ""}`} onClick={() => setType("total")}>
							Precio Total
						</button>
						<button className={`${classes.toggleButton} ${type === "ha" ? classes.active : ""}`} onClick={() => setType("ha")}>
							Precio Ha
						</button>
					</div>

					<div className={classes.filtersForm}>
						<div className={classes.filtersSelect}>
							<div data-rbd-drag-handle-disabled className={classes.selectWrapper}>
								<Select
									isMulti
									placeholder="Filtrar por especie"
									options={allSpecies}
									value={selectedSpecies}
									onChange={setSelectedSpecies}
									className={classes.select}
									menuPortalTarget={document.body}
									styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
									components={{ NoOptionsMessage }}
								/>
							</div>
							<div data-rbd-drag-handle-disabled className={classes.selectWrapper}>
								<Select
									isMulti
									placeholder="Filtrar por variedad"
									options={filteredVarieties}
									value={selectedVarieties}
									onChange={setSelectedVarieties}
									className={classes.select}
									menuPortalTarget={document.body}
									styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
									components={{ NoOptionsMessage }}
								/>
							</div>
						</div>
					</div>

					<ResponsiveContainer width="100%" height={300}>
						<BarChart
							data={chartData}
							margin={{ top: 20, right: 20, left: 0, bottom: 20 }}
							onClick={(e) => {
								if (e && e.activePayload && e.activePayload[0]) {
									const index = chartData.findIndex((d) => d.name === e.activePayload[0].payload.name);
									const id = finalIds[index];
									navigate({
										pathname: "/historiales/usos",
										search: `business_center_id=${id}&date_from=${filters.initial_date}&date_to=${filters.final_date}`,
									});
								}
							}}
						>
							<XAxis dataKey="name" tick={{ fontSize: 11, angle: -35, dy: 30 }} interval="preserveStartEnd" height={70} />
							<YAxis tickFormatter={formatShortNumber} tick={{ fontSize: 12 }} />
							<CartesianGrid strokeDasharray="3 3" vertical={false} />
							<Tooltip
								content={({ active, payload, label }) => {
									if (active && payload && payload.length) {
										return (
											<div style={{ backgroundColor: "#fff", border: "1px solid #ccc", padding: "8px", borderRadius: "6px" }}>
												<p style={{ margin: 0, fontSize: "13px", fontWeight: 500, color: "#374151" }}>
													{label}: ${formatMilesDecimalNew(payload[0].value.toString().replace(".", ","))}
												</p>
											</div>
										);
									}
									return null;
								}}
							/>
							<Bar dataKey="value" />
						</BarChart>
					</ResponsiveContainer>
				</>
			)}
		</>
	);
};

export default DashboardUsedBC;
